import React, { useState, useEffect } from "react";
import {
  Button,
  TextField,
  MenuItem,
  Alert,
  CircularProgress,
  Box,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
} from "@mui/material";
import { fetchUserId, fetchCompanyId } from "../../helpers/CompanyId";
import Navbar from "../../pages/Navbar";
import api from "../../api";
import { months } from "../../helpers/constants";

const PayslipGeneratorAll = () => {
  const [companyId, setCompanyId] = useState("");
  const [selection, setSelection] = useState("all");
  const [empId, setEmpId] = useState("");
  const [month, setMonth] = useState("");
  const [year, setYear] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [pdfUrl, setPdfUrl] = useState("");

  // Fetch companyId on component mount
  useEffect(() => {
    const fetchCompanyDetails = async () => {
      try {
        const userId = await fetchUserId();
        if (!userId) {
          setError("User ID not found");
          return;
        }

        const fetchedCompanyId = await fetchCompanyId(userId);
        if (!fetchedCompanyId) {
          setError("Company ID not found");
          return;
        }

        setCompanyId(fetchedCompanyId);
      } catch (err) {
        console.error(err);
        setError("Failed to fetch company details");
      }
    };

    fetchCompanyDetails();
  }, []);

  // Handle API Call to generate payslips
  const generatePayslips = async () => {
    if (!month || !year || !companyId || (selection === "specific" && !empId)) {
      setError("Please fill all required fields");
      return;
    }

    setLoading(true);
    setError("");
    setPdfUrl("");

    try {
      let apiUrl = "";

      const formattedMonth = String(month).padStart(2, "0"); 

      if (selection === "all") {
        apiUrl = `/api/payslips/${formattedMonth}-${year}/${companyId}/`;
      } else if (selection === "specific") {
        apiUrl = `/api/payslip/${empId}/${formattedMonth}-${year}/${companyId}/`;
      }

      const response = await api.get(apiUrl, { responseType: "blob" });

      // Create a blob URL to display the PDF
      const pdfBlob = new Blob([response.data], { type: "application/pdf" });
      const pdfBlobUrl = URL.createObjectURL(pdfBlob);
      setPdfUrl(pdfBlobUrl);
    } catch (err) {
      console.error(err);
      setError("Failed to generate payslips");
    }

    setLoading(false);
  };

  // Handle Download
  const downloadPdf = () => {
    const link = document.createElement("a");
    link.href = pdfUrl;
    const filename =
      selection === "all"
        ? `payslips_${year}_${month}.pdf`
        : `payslip_${empId}_${year}_${month}.pdf`;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div>
      <Navbar />

      {/* Selection Section */}
      <Box sx={{ margin: "50px auto", maxWidth: 600 }}>
        {error && (
          <Alert severity="error" sx={{ marginBottom: 2 }}>
            {error}
          </Alert>
        )}
        <FormControl component="fieldset" sx={{ marginBottom: 3 }}>
          <FormLabel component="legend">Generate Payslips For</FormLabel>
          <RadioGroup
            row
            value={selection}
            onChange={(e) => setSelection(e.target.value)}
          >
            <FormControlLabel
              value="all"
              control={<Radio />}
              label="All Employees"
            />
            <FormControlLabel
              value="specific"
              control={<Radio />}
              label="Specific Employee"
            />
          </RadioGroup>
        </FormControl>

        <Box
          display="flex"
          alignItems="center"
          gap={2}
          sx={{ marginBottom: 2 }}
        >
          {/* Input Fields based on Selection */}
          {selection === "specific" && (
            <TextField
              label="Employee ID"
              value={empId}
              onChange={(e) => setEmpId(e.target.value)}
              sx={{ width: "200px" }}
            />
          )}

          {/* Month and Year Fields */}
          <TextField
            select
            label="Month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            sx={{ width: "200px" }}
          >
            {months.map((monthName, index) => (
              <MenuItem key={index + 1} value={index + 1}>
                {monthName}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            label="Year"
            value={year}
            onChange={(e) => setYear(e.target.value)}
            sx={{ width: "200px" }}
          >
            {Array.from(
              { length: 5 },
              (_, i) => new Date().getFullYear() - i
            ).map((yr) => (
              <MenuItem key={yr} value={yr}>
                {yr}
              </MenuItem>
            ))}
          </TextField>

          <Button
            variant="contained"
            color="primary"
            onClick={generatePayslips}
            disabled={loading}
            sx={{ height: "54px" }}
          >
            {loading ? <CircularProgress size={24} /> : "Generate Payslips"}
          </Button>
        </Box>
      </Box>

      {/* Payslip Preview Box */}
      {pdfUrl && (
        <Box
          sx={{
            maxWidth: 800,
            margin: "50px auto",
            padding: 4,
            boxShadow: 3,
            borderRadius: 2,
            backgroundColor: "#fff",
          }}
        >
          <iframe
            src={pdfUrl}
            title="Payslip Preview"
            style={{ width: "100%", height: "500px", border: "1px solid #ccc" }}
          />
          <Button
            variant="contained"
            color="secondary"
            onClick={downloadPdf}
            sx={{ marginTop: 2 }}
          >
            Download PDF
          </Button>
        </Box>
      )}
    </div>
  );
};

export default PayslipGeneratorAll;
